import {
  ApolloCache,
  DefaultContext,
  FetchResult,
  gql,
  MutationUpdaterFunction,
  OperationVariables,
  useMutation,
} from '@apollo/client';
import {
  formatMembershipName,
  serviceLevelNameToPlatformSlaId,
} from '@provision/service-level-utils';
import { useCallback } from 'react';
import { generateFieldModifiersMap } from 'src/graphql/utils';
import { useNotifications } from 'src/utils/hooks';
import { SubscriptionPhase, SubscriptionStatus } from './address-subscription/types';

export interface ScheduleCreationParams {
  partnerId: number;
  addressId: string;
  cpp?: {
    partnerProductId: string;
    expDate: string;
  };
  preSoldMembership?: {
    partnerProductId: string;
    intervalCount: number;
    intervalUnit: 'year' | 'month';
    amount: number;
  };
  oneTimeTrial?: boolean;
}

const CREATE_STRIPE_SUBSCRIPTION_SCHEDULE_MUTATION = gql`
  mutation CreateSubscriptionSchedule(
    $partnerId: Int!
    $addressId: String!
    $cpp: CreateSubscriptionScheduleCPPInput
    $preSoldMembership: CreateSubscriptionSchedulePreSoldMembershipInput
    $oneTimeTrial: Boolean
  ) {
    createSubscriptionSchedule(
      params: {
        partnerId: $partnerId
        addressId: $addressId
        cpp: $cpp
        preSoldMembership: $preSoldMembership
        oneTimeTrial: $oneTimeTrial
      }
    ) {
      subscriptionId
      addressId
      cppExpDate
      serviceLevel
      success
    }
  }
`;

export interface CreateStripeSubscriptionScheduleMutation {
  createSubscriptionSchedule: {
    addressId: string;
    cppExpDate?: string;
    serviceLevel?: string;
    subscriptionId?: string;
    success: boolean;
  };
}

export type ScheduleCreator = (
  params: ScheduleCreationParams,
) => Promise<FetchResult<CreateStripeSubscriptionScheduleMutation>> | undefined;

export function formatServiceLevel(serviceLevel: string): string {
  return `CPP ${formatMembershipName(serviceLevel)}`;
}

// The backend rejects ineligible one-time trials with actionable copy
// ("already used its one-time trial", "only ... non-member addresses").
// Surface that instead of the generic retry message - retrying cannot succeed.
const ONE_TIME_TRIAL_REJECTION_PATTERN = /one-time trial/i;

export function getScheduleCreationErrorMessage(error: {
  graphQLErrors?: readonly { message: string }[];
}): string {
  const rejection = error.graphQLErrors?.find((graphQLError) =>
    ONE_TIME_TRIAL_REJECTION_PATTERN.test(graphQLError.message),
  );

  return (
    rejection?.message ??
    'Something went wrong when creating the subscription schedule, please try again'
  );
}

const applyCppScheduleToCache =
  (
    params: ScheduleCreationParams,
  ): MutationUpdaterFunction<
    CreateStripeSubscriptionScheduleMutation,
    OperationVariables,
    DefaultContext,
    ApolloCache<unknown>
  > =>
  (cache, { data }) => {
    if (!data?.createSubscriptionSchedule.success) return;

    const id = cache.identify({
      __typename: 'Address',
      id: data.createSubscriptionSchedule.addressId,
    });
    if (!id) return;

    const fields = generateFieldModifiersMap({
      cppExpDate: data.createSubscriptionSchedule.cppExpDate,
      chargifySubId: data.createSubscriptionSchedule.subscriptionId,
      clientActivatedMembership: data.createSubscriptionSchedule.serviceLevel,
      platformSlaId: serviceLevelNameToPlatformSlaId(
        data.createSubscriptionSchedule.serviceLevel,
      ),
      addressSubscriptions: [
        {
          __typename: 'AddressSubscription',
          id: 'temp-cpp-sub-id',
          addressSubscriptionId: 'temp-cpp-sub-id',
          stripeSubscriptionId: data.createSubscriptionSchedule.subscriptionId!,
          partnerId: params.partnerId,
          addressId: params.addressId,
          phase: SubscriptionPhase.Trial,
          status: SubscriptionStatus.Trialing,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
        },
      ],
    });

    cache.modify({
      id,
      fields,
    });

    cache.evict({
      id: 'ROOT_QUERY',
      fieldName: 'getStripeSubscriptionDetails',
    });
  };

export const useStripeSubscriptionSchedule = () => {
  const { showErrorAlert, showSuccessAlert } = useNotifications();
  const [createSubscriptionScheduleMutation, { loading }] =
    useMutation<CreateStripeSubscriptionScheduleMutation>(
      CREATE_STRIPE_SUBSCRIPTION_SCHEDULE_MUTATION,
      {
        onError: (error) => {
          console.error(error);
          showErrorAlert(getScheduleCreationErrorMessage(error));
        },
      },
    );

  const addComplimentaryPeriod = useCallback(
    (params: ScheduleCreationParams) => {
      if (!params.addressId || !params.partnerId) return;

      return createSubscriptionScheduleMutation({
        variables: {
          ...params,
        },
        onCompleted: () => {
          showSuccessAlert('Complimentary period added successfully');
        },
        update: applyCppScheduleToCache(params),
      });
    },
    [createSubscriptionScheduleMutation, showSuccessAlert],
  );

  // The one-time trial rides the complimentary-period rails; the flag makes the
  // backend gate on the pilot's partner-group rule and emit the Iterable event.
  const activateTrialMembership = useCallback(
    (params: ScheduleCreationParams) => {
      if (!params.addressId || !params.partnerId) return;

      return createSubscriptionScheduleMutation({
        variables: {
          ...params,
          oneTimeTrial: true,
        },
        onCompleted: () => {
          showSuccessAlert('Trial membership activated successfully');
        },
        update: applyCppScheduleToCache(params),
      });
    },
    [createSubscriptionScheduleMutation, showSuccessAlert],
  );

  const addPreSoldMembership = useCallback(
    (params: ScheduleCreationParams) => {
      if (!params.addressId || !params.partnerId) return;

      return createSubscriptionScheduleMutation({
        variables: {
          ...params,
        },
        onCompleted: () => {
          showSuccessAlert('Pre-sold membership added successfully');
        },
        update: (cache, { data }) => {
          if (!data?.createSubscriptionSchedule?.success) return;

          const id = cache.identify({
            __typename: 'Address',
            id: data.createSubscriptionSchedule.addressId,
          });

          const fields = generateFieldModifiersMap({
            chargifySubId: data.createSubscriptionSchedule.subscriptionId,
            addressSubscriptions: [
              {
                __typename: 'AddressSubscription',
                id: 'temp-psm-sub-id',
                addressSubscriptionId: 'temp-psm-sub-id',
                stripeSubscriptionId: data.createSubscriptionSchedule.subscriptionId!,
                partnerId: params.partnerId,
                addressId: params.addressId,
                phase: SubscriptionPhase.Psm,
                status: SubscriptionStatus.Trialing,
                createdAt: new Date().toISOString(),
                updatedAt: new Date().toISOString(),
              },
            ],
            preSoldMemberships: [
              {
                __typename: 'PreSoldMembership',
                id: 'temp-pre-sold-id',
                preSoldMembershipId: 'temp-pre-sold-id',
                isScheduled: true,
              },
            ],
          });

          cache.modify({
            id,
            fields,
          });

          cache.evict({
            id: 'ROOT_QUERY',
            fieldName: 'getStripeSubscriptionDetails',
          });
        },
      });
    },
    [createSubscriptionScheduleMutation, showSuccessAlert],
  );

  return {
    addComplimentaryPeriod,
    activateTrialMembership,
    addPreSoldMembership,
    loading,
  };
};
